/**
 * The syllabus builder's state — pure, so every rule here is testable.
 *
 * A syllabus is one of two formats (CourseSyllabusTemplate.syllabusType):
 *   • 'module' — Modules → Topics (each with minutes) → Subtopics. A batch's
 *                classes are FORMED from the topics at its class length
 *                (classPlanner.js previews them).
 *   • 'day'    — Days, each with topics. Each day is one class.
 *
 * Every item carries `uid` (the editor's own id, for React keys and focus) and
 * `key` (the server's stable id, utils/syllabusKeys — absent until first saved).
 * Keys go back on save so links into the syllabus (materials, topic resources)
 * survive reordering and renaming. The server also re-finds a missing key by
 * title, so an import or an AI draft keeps its links too.
 */

import { DEFAULT_TOPIC_MINUTES, clampClassMinutes } from "./classPlanner";

let counter = 0;
export const uid = (prefix = "n") => `${prefix}${Date.now().toString(36)}${(counter += 1).toString(36)}`;

export const LIMITS = { title: 200, subtopic: 200, minutes: 600 };

const text = (v, max = LIMITS.title) => String(v ?? "").slice(0, max);
const minutesOf = (v, fallback = DEFAULT_TOPIC_MINUTES) => {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n > 0 ? Math.min(n, LIMITS.minutes) : fallback;
};

export const blankTopic = (title = "") => ({
  uid: uid("t"),
  key: undefined,
  title,
  minutes: DEFAULT_TOPIC_MINUTES,
  subtopics: [],
  startsNewClass: false,
  classTitle: "",
});

export const blankModule = (title = "") => ({ uid: uid("m"), key: undefined, title, topics: [blankTopic()] });

export const blankDayTopic = (title = "") => ({ uid: uid("dt"), title });

export const blankDay = (title = "") => ({
  uid: uid("d"),
  key: undefined,
  title,
  minutes: DEFAULT_TOPIC_MINUTES,
  topics: [blankDayTopic()],
});

export const emptySyllabus = (format = "module") => ({ format, classMinutes: null, modules: [], days: [] });

/** A saved template → editor state. */
export function fromTemplate(template) {
  if (!template) return emptySyllabus();
  const format = template.syllabusType === "module" ? "module" : "day";
  return {
    format,
    classMinutes: clampClassMinutes(template.classMinutes),
    modules: [...(template.modules || [])]
      .sort((a, b) => a.moduleNumber - b.moduleNumber)
      .map((m) => ({
        uid: uid("m"),
        key: m.key,
        title: m.title || "",
        topics: (m.topics || []).map((t) => ({
          uid: uid("t"),
          key: t.key,
          title: t.title || "",
          minutes: minutesOf(t.estimatedDuration),
          subtopics: (t.subtopics || []).map(String).filter(Boolean),
          startsNewClass: !!t.startsNewClass,
          classTitle: t.classTitle || "",
        })),
      })),
    days: [...(template.days || [])]
      .sort((a, b) => a.dayNumber - b.dayNumber)
      .map((d) => ({
        uid: uid("d"),
        key: d.key,
        title: d.title || "",
        minutes: minutesOf(d.estimatedDuration),
        topics: (d.topics || []).map((t) => blankDayTopic(String(t))),
      })),
  };
}

/** Editor state → POST /syllabus/template body. */
export function toPayload(state, courseId) {
  const payload = { course: courseId, syllabusType: state.format, classMinutes: clampClassMinutes(state.classMinutes) };
  if (state.format === "day") {
    payload.days = state.days.map((d, i) => ({
      key: d.key,
      dayNumber: i + 1,
      title: d.title.trim(),
      topics: d.topics.map((t) => t.title.trim()).filter(Boolean),
      estimatedDuration: minutesOf(d.minutes),
    }));
  } else {
    payload.modules = state.modules.map((m, i) => ({
      key: m.key,
      moduleNumber: i + 1,
      title: m.title.trim(),
      topics: m.topics.map((t) => ({
        key: t.key,
        title: t.title.trim(),
        estimatedDuration: minutesOf(t.minutes),
        subtopics: t.subtopics.map((s) => s.trim()).filter(Boolean),
        startsNewClass: t.startsNewClass || undefined,
        classTitle: t.classTitle.trim() || undefined,
      })),
    }));
  }
  return payload;
}

/**
 * After a save: the server's keys (and class length) onto the editor's items,
 * by position — the payload was sent in the editor's order — so what is open
 * and focused stays put, and the new keys are what the next save sends back.
 */
export function withSavedKeys(state, template) {
  if (!template) return state;
  const sortedModules = [...(template.modules || [])].sort((a, b) => a.moduleNumber - b.moduleNumber);
  const sortedDays = [...(template.days || [])].sort((a, b) => a.dayNumber - b.dayNumber);
  return {
    ...state,
    classMinutes: clampClassMinutes(template.classMinutes),
    modules: state.format === "module"
      ? state.modules.map((m, i) => ({
        ...m,
        key: sortedModules[i]?.key || m.key,
        topics: m.topics.map((t, j) => ({ ...t, key: sortedModules[i]?.topics?.[j]?.key || t.key })),
      }))
      : state.modules,
    days: state.format === "day"
      ? state.days.map((d, i) => ({ ...d, key: sortedDays[i]?.key || d.key }))
      : state.days,
  };
}

/** Has the editor moved away from what is saved? Compares what would be sent. */
export const isDirty = (state, saved) =>
  JSON.stringify(toPayload(state, "")) !== JSON.stringify(toPayload(saved, ""));

/**
 * What stops a save, keyed by the item's uid so the editor can mark it, plus
 * the same in reading order. An empty list means the syllabus can be saved.
 */
export function validate(state) {
  const list = [];
  const add = (id, message) => list.push({ uid: id, message });
  if (state.format === "day") {
    if (!state.days.length) add("syllabus", "Add at least one day.");
    state.days.forEach((d, i) => {
      if (!d.title.trim()) add(d.uid, `Day ${i + 1} needs a title.`);
      if (!d.topics.some((t) => t.title.trim())) add(`${d.uid}:topics`, `Day ${i + 1} needs at least one topic.`);
    });
  } else {
    if (!state.modules.length) add("syllabus", "Add at least one module.");
    state.modules.forEach((m, i) => {
      if (!m.title.trim()) add(m.uid, `Module ${i + 1} needs a title.`);
      if (!m.topics.length) add(`${m.uid}:topics`, `Module ${i + 1} needs at least one topic.`);
      m.topics.forEach((t, j) => {
        if (!t.title.trim()) add(t.uid, `Topic ${j + 1} of module ${i + 1} needs a title.`);
      });
    });
  }
  const byUid = {};
  for (const e of list) if (!byUid[e.uid]) byUid[e.uid] = e.message;
  return { list, byUid };
}

/** Counts for the summary bar. */
export function syllabusStats(state) {
  if (state.format === "day") {
    return {
      groups: state.days.length,
      topics: state.days.reduce((n, d) => n + d.topics.filter((t) => t.title.trim()).length, 0),
      subtopics: 0,
      minutes: state.days.reduce((n, d) => n + minutesOf(d.minutes, 0), 0),
    };
  }
  let topics = 0;
  let subtopics = 0;
  let minutes = 0;
  for (const m of state.modules) {
    for (const t of m.topics) {
      if (!t.title.trim()) continue;
      topics += 1;
      subtopics += t.subtopics.filter((s) => s.trim()).length;
      minutes += minutesOf(t.minutes);
    }
  }
  return { groups: state.modules.length, topics, subtopics, minutes };
}

/** 90 → "1h 30m". */
export const formatMinutes = (mins) => {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
};

/** Move the item at `index` by `delta` (−1 up, +1 down). Out of range → unchanged. */
export function moveItem(list, index, delta) {
  const to = index + delta;
  if (to < 0 || to >= list.length) return list;
  const out = [...list];
  const [item] = out.splice(index, 1);
  out.splice(to, 0, item);
  return out;
}

// ── Import and AI drafts ─────────────────────────────────────────────────────
// The import dialog and the AI generator speak the template's own shape
// ({ moduleNumber, title, topics: [{ title, estimatedDuration, subtopics }] } /
// { dayNumber, title, topics: [string], estimatedDuration }).

/** Editor state → the template shape the import dialog compares against. */
export function toGroups(state) {
  const payload = toPayload(state, "");
  return state.format === "day" ? payload.days : payload.modules;
}

const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();

/**
 * Groups from an import or an AI draft → editor state. An item recognisably the
 * same as one already in the editor (same title — a topic within the same
 * module first) keeps its key, so its links and resources stay with it.
 */
export function fromGroups(format, groups, previous) {
  if (format === "day") {
    const prevDays = new Map((previous?.days || []).filter((d) => d.key).map((d) => [norm(d.title), d.key]));
    const used = new Set();
    return {
      ...(previous || emptySyllabus()),
      format: "day",
      days: (groups || []).map((g) => {
        const key = prevDays.get(norm(g.title));
        const keep = key && !used.has(key) ? key : undefined;
        if (keep) used.add(keep);
        return {
          uid: uid("d"),
          key: keep,
          title: text(g.title),
          minutes: minutesOf(g.estimatedDuration),
          topics: (Array.isArray(g.topics) && g.topics.length ? g.topics : [""])
            .map((t) => blankDayTopic(text(typeof t === "string" ? t : t?.title))),
        };
      }),
    };
  }

  const prevModules = previous?.modules || [];
  const prevByTitle = new Map(prevModules.map((m) => [norm(m.title), m]));
  const anyTopic = new Map();
  for (const m of prevModules) for (const t of m.topics) if (t.key && !anyTopic.has(norm(t.title))) anyTopic.set(norm(t.title), t.key);
  const used = new Set();
  const claim = (k) => {
    if (!k || used.has(k)) return undefined;
    used.add(k);
    return k;
  };

  return {
    ...(previous || emptySyllabus()),
    format: "module",
    modules: (groups || []).map((g) => {
      const prev = prevByTitle.get(norm(g.title));
      const prevTopics = new Map((prev?.topics || []).filter((t) => t.key).map((t) => [norm(t.title), t.key]));
      return {
        uid: uid("m"),
        key: claim(prev?.key),
        title: text(g.title),
        topics: (g.topics || []).map((t) => ({
          ...blankTopic(text(t.title)),
          key: claim(prevTopics.get(norm(t.title))) || claim(anyTopic.get(norm(t.title))),
          minutes: minutesOf(t.estimatedDuration),
          subtopics: (Array.isArray(t.subtopics) ? t.subtopics : []).map((s) => text(s, LIMITS.subtopic)).filter(Boolean),
        })),
      };
    }),
  };
}
